import * as React from "react";
import Box from "@mui/material/Box";
import "./Header.css";
import {
  Button,
  Card,
  Collapse,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import logo from "../../images/songjukti_logo.png";
import {
  ArrowRightAlt,
  Close,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Menu as MenuIcon,
  Search,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { categoryApi, menuBarApi } from "../API/Api";
import CardItem from "./CardItem";
import { useStore } from "../../store/useStore";
import { tokens } from "../../store/tokens";
// NEW: adjust the path to wherever you saved OpenUserPortalButton.jsx
import { openUserPortal } from "./OpenUserPortalButton";

const drawerWidth = 260;
const USER_PORTAL_HOME = "https://user.songjukti.com/";

/*
 * RESPONSIVE LAYOUT MAP (MUI default breakpoints)
 *   xs   0-599    logo + search icon + hamburger. Search expands below bar.
 *   sm   600-899  same as xs, slightly roomier padding.
 *   md   900-1199 logo + inline search bar + Sign in/Sign up + hamburger.
 *   lg   1200+    full desktop: search + mega-menu nav + auth buttons.
 *
 * The mega menus are hover-based and are too wide for tablets, so the nav
 * collapses into the drawer below `lg`.
 */
const HEADER_HEIGHT = { xs: 60, md: 72 };

const decodeLocalData = (name) => {
  try {
    const encoded = window.localStorage.getItem(name);
    if (!encoded) return null;

    const jsonString = atob(encoded); // decode base64 to string
    return JSON.parse(jsonString); // parse JSON string
  } catch (error) {
    console.error("Failed to decode localStorage data:", error);
    return null;
  }
};

// Shared "nav item with dropdown" text treatment, so Find Work / Find
// Expert / Why Songjukti all match exactly.
const navLabelSx = {
  color: tokens.color.ink,
  fontFamily: tokens.font.body,
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
  padding: "26px 4px",
  transition: "color 0.2s ease",
  whiteSpace: "nowrap",
};

const navCaretSx = {
  color: tokens.color.inkMuted,
  fontSize: "1.15rem",
  marginLeft: "-2px",
  transition: "transform 0.25s ease, color 0.2s ease",
};

// One shared look for the tile-style links inside the mega menus.
const menuTileSx = {
  padding: 2,
  borderRadius: `${tokens.radius.sm}px`,
  transition: "background-color 0.18s ease",
  "&:hover": {
    bgcolor: tokens.color.tealLight,
    cursor: "pointer",
  },
};

// Shared look for the mega-menu dropdown panels. maxHeight + overflowY keep
// the panel usable on short desktop/laptop screens instead of running off
// the bottom of the viewport.
const megaMenuSx = {
  position: "absolute",
  top: "100%",
  left: 0,
  zIndex: 2,
  bgcolor: tokens.bg.surface,
  width: "100%",
  borderRadius: 0,
  borderTop: `1px solid ${tokens.color.line}`,
  boxShadow: tokens.shadow.lg,
  display: "none",
  maxHeight: `calc(100vh - ${HEADER_HEIGHT.md}px)`,
  overflowY: "auto",
  // overflowY:auto turns overflowX into auto too; the negative margins that
  // Grid `spacing` adds would then show a sideways scrollbar.
  overflowX: "hidden",
};

const Header = () => {
  const appBarRef = useRef();
  const { user, setUser } = useStore();

  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [searchType, setSearchType] = useState("");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [categoryData, setCategoryData] = useState([]);
  const [showData, setShowData] = useState(1);
  const [menuBarData, setMenuBarData] = useState([]);

  const [isMember, setIsMember] = useState(false);
  const [isLogged, setIsLogged] = useState(false);

  const allowedOrigins = [
    window.location.origin,
    "http://songjukti.com",
    "https://songjukti.com",
    "http://user.songjukti.com",
    "https://user.songjukti.com",
  ];

  useEffect(() => {
    // 1. Evaluate user from global state
    const isValidMember = !!(user?.refer && user?.sopnoid && user?.membership);
    const isValidLogin = !!user?.sopnoid;

    setIsMember(isValidMember);
    setIsLogged(isValidLogin);

    // 2. Check localStorage for stored user, only if not already logged in
    const storedUser = decodeLocalData("gh_sl@g");

    if (!isValidLogin && storedUser) {
      try {
        const parsedUser = storedUser;
        const originMatches = allowedOrigins.includes(parsedUser?.origin);
        const hasValidId = !!parsedUser?.sopnoid;

        if (originMatches && hasValidId) {
          const isValidLoginFromStorage = !!parsedUser?.sopnoid;
          const isValidMemberFromStorage = !!(
            parsedUser?.refer &&
            parsedUser?.sopnoid &&
            parsedUser?.membership
          );

          setUser(parsedUser);
          setIsLogged(isValidLoginFromStorage);
          setIsMember(isValidMemberFromStorage);
        } else {
          console.warn("Origin mismatch or invalid user — clearing session");
          localStorage.removeItem("user");
        }
      } catch (err) {
        console.error("Failed to parse stored user", err);
        localStorage.removeItem("user");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    fetch(menuBarApi)
      .then((res) => res.json())
      .then((result) => setMenuBarData(result))
      .catch((error) => console.log("menuBarData Error => ", error));
  }, []);

  useEffect(() => {
    fetch(categoryApi)
      .then((res) => res.json())
      .then((result) => setCategoryData(result.slice(0, 6)))
      .catch((error) => console.log("categoryData Error => ", error));
  }, []);

  const handleChange = (event) => setSearch(event.target.value);
  const handleTypeChange = (event) => setSearchType(event.target.value);

  const handleSearchSubmit = () => {
    const query = search.trim();
    if (!query) return;
    navigate(`/search/${searchType || "all"}/${encodeURIComponent(query)}`);
    setSearch("");
    setMobileSearchOpen(false);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearchSubmit();
    }
  };

  // "Sign in" / "Dashboard" button. Logged in on the main portal -> always
  // open the user portal's /sso-login page (valid 2 minutes). If the user
  // ids are missing, the page falls back to the shared SSO cookie.
  const handleDashboard = () => {
    if (!isLogged) {
      window.open(USER_PORTAL_HOME);
      return;
    }

    const u = user?.sopnoid ? user : decodeLocalData("gh_sl@g");
    openUserPortal({
      sopnoid: u?.sopnoid,
      mobilenumber: u?.umobile || u?.mobilenumber, // login response key: umobile
      uReferId: u?.uReferId, // adjust if your stored user uses another key
    });
  };

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        textAlign: "left",
        height: "100%",
        bgcolor: tokens.bg.surface,
        overflowY: "auto",
        // keep content clear of notches / home indicators on phones
        pb: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{ padding: "18px 20px" }}
      >
        <Box
          component="img"
          src={logo}
          alt="Songjukti logo"
          sx={{ width: 48, cursor: "pointer" }}
          onClick={() => navigate("/")}
        />
      </Stack>
      <Divider sx={{ borderColor: tokens.color.line }} />
      <List sx={{ py: 1 }}>
        {menuBarData?.map((element) => (
          <ListItem key={element.id} disablePadding>
            <ListItemButton
              onClick={() => navigate("/explore/" + element.id)}
              sx={{ px: 3, py: 1.25 }}
            >
              <ListItemText
                primary={element?.title}
                primaryTypographyProps={{
                  fontFamily: tokens.font.body,
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  color: tokens.color.ink,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: tokens.color.line }} />
      <Stack spacing={1.5} sx={{ p: 3 }}>
        {!isLogged && (
          <Button
            fullWidth
            variant="contained"
            sx={{ bgcolor: tokens.color.ink, "&:hover": { bgcolor: tokens.color.teal } }}
            onClick={() => window.open("https://user.songjukti.com/signup")}
          >
            Register
          </Button>
        )}
        <Button
          fullWidth
          variant="contained"
          sx={{
            bgcolor: tokens.color.teal,
            "&:hover": { bgcolor: tokens.color.tealDark },
          }}
          onClick={handleDashboard}
        >
          {isLogged ? "Dashboard" : "Sign in"}
        </Button>
      </Stack>
    </Box>
  );

  // Reusable mega-menu tile: title + one-line description, shared by the
  // "Find Work" and "Why Songjukti" dropdowns.
  const InfoTile = ({ title, description, onClick }) => (
    <Paper elevation={0} sx={menuTileSx} onClick={onClick}>
      <Stack spacing={0.5}>
        <Typography
          sx={{
            fontFamily: tokens.font.body,
            fontSize: "0.95rem",
            fontWeight: 600,
            color: tokens.color.ink,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: tokens.font.body,
            fontSize: "0.8rem",
            color: tokens.color.inkMuted,
            lineHeight: 1.5,
          }}
        >
          {description}
        </Typography>
      </Stack>
    </Paper>
  );

  const expertPanels = {
    1: {
      eyebrow: "Talent Marketplace",
      title: "Post a job and hire a pro",
      copy: "Browse and buy projects that have a clear scope and price.",
      cta: "Hire on Talent Marketplace",
    },
    2: {
      eyebrow: "Project Catalog",
      title: "Browse and buy projects",
      copy: "Browse and buy projects that have a clear scope and price.",
      cta: "Browse Project Catalog",
    },
    3: {
      eyebrow: "Talent Scout",
      title: "Post a service and sell it",
      copy: "Browse and buy projects that have a clear scope and price.",
      cta: "Talk to Talent Scout",
    },
  };

  const activePanel = expertPanels[showData];

  return (
    <Box sx={{ display: "block", position: "relative", width: "100%", maxWidth: "100%" }}>
      <Box
        component="header"
        ref={appBarRef}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          bgcolor: tokens.bg.surface,
          borderBottom: `1px solid ${tokens.color.line}`,
          boxShadow: tokens.shadow.sm,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            padding: { xs: "0 12px", sm: "0 20px", md: "0 24px" },
            minHeight: HEADER_HEIGHT,
            gap: { xs: 1, md: 2 },
          }}
        >
          {/* Logo + search */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 1, md: 2 }}
            sx={{ minWidth: 0 }}
          >
            <Box
              component="img"
              sx={{ width: { xs: 42, md: 52 }, flexShrink: 0, cursor: "pointer" }}
              alt="Songjukti logo"
              src={logo}
              onClick={() => navigate("/")}
            />

            <FormControl
              sx={{
                display: { xs: "none", md: "block" },
                // Shrinks on tablets / small laptops so the bar never
                // pushes the auth buttons off-screen.
                minWidth: { md: 220, lg: 280, xl: 340 },
                maxWidth: "100%",
                // bgcolor: tokens.input.bgMuted,
                borderRadius: `${tokens.radius.pill}px`,
                border: `1px solid ${tokens.input.border}`,
                overflow: "hidden",
                boxSizing: "border-box",
                transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  borderColor: tokens.input.borderHover,
                },
                "&:focus-within": {
                  borderColor: tokens.input.borderFocus,
                  boxShadow: `0 0 0 3px ${tokens.color.tealLight}`,
                },
                // The Select/TextField beneath each carry their own small
                // corner radius from the theme defaults; without clipping
                // here that radius pokes through the pill's rounded edge.
                "& .MuiSelect-select, & .MuiInputBase-root": {
                  borderRadius: 0,
                },
              }}
            >
              <Stack
                direction="row"
                spacing={0}
                justifyContent="left"
                alignItems="center"
              >
                <Select
                  variant="standard"
                  value={searchType}
                  onChange={handleTypeChange}
                  displayEmpty
                  disableUnderline
                  size="small"
                  inputProps={{ "aria-label": "Search category" }}
                  sx={{
                    pl: 2,
                    minWidth: 90,
                    fontFamily: tokens.font.body,
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    color: tokens.color.inkMuted,

                    "& .MuiSelect-select": {
                      py: 1,
                      backgroundColor: "transparent",
                    },

                    "&:hover": {
                      backgroundColor: "transparent",
                    },

                    "&.Mui-focused": {
                      backgroundColor: "transparent",
                    },

                    "& .MuiSelect-select:focus": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="job">Job</MenuItem>
                  <MenuItem value="service">Service</MenuItem>
                  <MenuItem value="freelancer">Freelancer</MenuItem>
                  <MenuItem value="agency">Agency</MenuItem>
                </Select>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ my: 1, borderColor: tokens.color.line }}
                />

                <TextField
                  variant="standard"
                  id="headerSearch"
                  name="headerSearch"
                  fullWidth
                  value={search}
                  sx={{
                    pl: 1.5,
                    minWidth: 0,
                    "& input": {
                      fontFamily: tokens.font.body,
                      fontSize: "0.875rem",
                      color: tokens.color.ink,
                    },
                  }}
                  onChange={handleChange}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={`Search ${searchType || "jobs"}`}
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear search"
                          size="small"
                          onClick={() => setSearch("")}
                          sx={{ color: tokens.color.inkFaint }}
                        >
                          <Close fontSize="inherit" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <IconButton
                  aria-label="search"
                  sx={{
                    m: "4px",
                    flexShrink: 0,
                    borderRadius: `${tokens.radius.pill}px`,
                    bgcolor: tokens.color.teal,
                    color: tokens.color.white,
                    "&:hover": { bgcolor: tokens.color.tealDark },
                  }}
                  onClick={handleSearchSubmit}
                >
                  <Search fontSize="small" />
                </IconButton>
              </Stack>
            </FormControl>
          </Stack>

          {/* Nav + actions (large desktop only: mega menus need the width) */}
          <Stack
            spacing={3}
            direction="row"
            alignItems="center"
            sx={{ display: { xs: "none", lg: "flex" } }}
          >
            {/* Find Work */}
            <Box className="item1">
              <Stack direction="row" alignItems="center" className="pass">
                <Typography sx={navLabelSx}>Find Work</Typography>
                <KeyboardArrowDown className="item1Icon" sx={navCaretSx} />
              </Stack>
              <Paper
                elevation={0}
                className="item1MenuList"
                sx={megaMenuSx}
              >
                <Grid container sx={{ maxWidth: 1200, mx: "auto" }}>
                  <Grid item xs={4} sx={{ p: 3 }}>
                    <InfoTile
                      title="Ways to earn"
                      description="Learn why Songjukti has the right opportunities for you."
                      onClick={() =>
                        window.open("https://community.songjukti.com/forum/")
                      }
                    />
                  </Grid>
                  <Grid item xs={4} sx={{ p: 3 }}>
                    <InfoTile
                      title="Find work for your skills"
                      description="Explore the kind of work available in your field."
                      onClick={() =>
                        window.open("https://community.songjukti.com/forum/")
                      }
                    />
                  </Grid>
                  <Grid item xs={4} sx={{ p: 3 }}>
                    <InfoTile
                      title="Find ways to promote yourself"
                      description="Show clients you're the one they want."
                      onClick={() =>
                        window.open("https://community.songjukti.com/forum/")
                      }
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Box>

            {/* Find Expert */}
            <Box className="item1">
              <Stack direction="row" alignItems="center" className="pass">
                <Typography sx={navLabelSx}>Find Expert</Typography>
                <KeyboardArrowDown className="item1Icon" sx={navCaretSx} />
              </Stack>
              <Paper
                elevation={0}
                className="item1MenuList"
                sx={megaMenuSx}
              >
                <Grid container sx={{ maxWidth: 1200, mx: "auto" }}>
                  <Grid item xs={4}>
                    <Stack spacing={1} sx={{ p: 3 }}>
                      {Object.entries(expertPanels).map(([key, panel]) => (
                        <Paper
                          key={key}
                          onMouseEnter={() => setShowData(Number(key))}
                          elevation={0}
                          sx={{
                            ...menuTileSx,
                            bgcolor:
                              showData === Number(key)
                                ? tokens.color.tealLight
                                : "transparent",
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Stack>
                              <Typography
                                sx={{
                                  fontFamily: tokens.font.body,
                                  fontSize: "0.9rem",
                                  fontWeight: 600,
                                  color: tokens.color.ink,
                                }}
                              >
                                {panel.title}
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: tokens.font.body,
                                  fontSize: "0.8rem",
                                  color: tokens.color.inkMuted,
                                }}
                              >
                                {panel.eyebrow}
                              </Typography>
                            </Stack>
                            <KeyboardArrowRight
                              sx={{ color: tokens.color.inkFaint }}
                            />
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Grid>

                  <Grid item xs={8}>
                    <Box
                      sx={{
                        borderLeft: `1px solid ${tokens.color.line}`,
                        height: "100%",
                        py: 3,
                        pl: 4,
                        pr: 3,
                      }}
                    >
                      <Grid container spacing={3}>
                        <Grid item xs={4}>
                          <Box
                            sx={{
                              height: 4,
                              width: 44,
                              borderRadius: `${tokens.radius.pill}px`,
                              bgcolor: tokens.color.teal,
                              mb: 2,
                            }}
                          />
                          <Stack spacing={1.5}>
                            <Typography
                              sx={{
                                fontFamily: tokens.font.heading,
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                color: tokens.color.ink,
                              }}
                            >
                              {activePanel.eyebrow}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: tokens.font.body,
                                fontSize: "0.85rem",
                                color: tokens.color.inkMuted,
                                lineHeight: 1.6,
                              }}
                            >
                              {activePanel.copy}
                            </Typography>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                              sx={{ cursor: "pointer" }}
                            >
                              <Typography
                                sx={{
                                  color: tokens.color.teal,
                                  fontFamily: tokens.font.body,
                                  fontSize: "0.8125rem",
                                  fontWeight: 600,
                                }}
                              >
                                {activePanel.cta}
                              </Typography>
                              <ArrowRightAlt
                                sx={{ color: tokens.color.teal, fontSize: "1.15rem" }}
                              />
                            </Stack>
                          </Stack>
                        </Grid>
                        <Grid item xs={8}>
                          <Grid container spacing={2}>
                            {categoryData.map((element) => (
                              <Grid item xs={4} key={element.refer}>
                                <CardItem categoryData={element} />
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>

            {/* Why Songjukti */}
            <Box className="item1">
              <Stack direction="row" alignItems="center" className="pass">
                <Typography sx={navLabelSx}>Why Songjukti</Typography>
                <KeyboardArrowDown className="item1Icon" sx={navCaretSx} />
              </Stack>
              <Paper
                elevation={0}
                className="item1MenuList"
                sx={megaMenuSx}
              >
                <Grid container sx={{ maxWidth: 1200, mx: "auto" }}>
                  <Grid item xs={6} sx={{ p: 3 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <InfoTile
                          title="Success Stories"
                          description="Learn why Songjukti has the right opportunities for you."
                          onClick={() =>
                            window.open("https://community.songjukti.com/forum/")
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoTile
                          title="Reviews"
                          description="Explore the kind of work available in your field."
                          onClick={() =>
                            window.open("https://community.songjukti.com/forum/")
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoTile
                          title="How to hire"
                          description="Learn why Songjukti has the right opportunities for you."
                          onClick={() =>
                            window.open("https://community.songjukti.com/forum/")
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoTile
                          title="How to find work"
                          description="Explore the kind of work available in your field."
                          onClick={() =>
                            window.open("https://community.songjukti.com/forum/")
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={6} sx={{ p: 3, borderLeft: `1px solid ${tokens.color.line}` }}>
                    <Stack spacing={2}>
                      <Typography
                        sx={{
                          fontFamily: tokens.font.body,
                          fontSize: "0.95rem",
                          fontWeight: 600,
                          color: tokens.color.ink,
                        }}
                      >
                        Where work gets done
                      </Typography>

                      {[
                        "Getting Started as a Freelancer",
                        "Getting Started as an Agency",
                        "Getting Started as a Buyer",
                      ].map((label) => (
                        <Card
                          key={label}
                          elevation={0}
                          sx={{
                            px: 2,
                            py: 1.25,
                            width: { lg: "80%", xl: "60%" },
                            border: `1px solid ${tokens.color.line}`,
                            borderRadius: `${tokens.radius.sm}px`,
                            transition: "border-color 0.18s ease, background-color 0.18s ease",
                            "&:hover": {
                              bgcolor: tokens.color.tealLight,
                              borderColor: tokens.color.teal,
                              cursor: "pointer",
                            },
                          }}
                          onClick={() => window.open("http://community.songjukti.com/")}
                        >
                          <Typography
                            sx={{
                              fontFamily: tokens.font.body,
                              fontSize: "0.75rem",
                              color: tokens.color.inkMuted,
                            }}
                          >
                            Guide
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: tokens.font.body,
                              fontSize: "0.875rem",
                              fontWeight: 600,
                              color: tokens.color.ink,
                            }}
                          >
                            {label}
                          </Typography>
                        </Card>
                      ))}

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ cursor: "pointer" }}
                        onClick={() => window.open("http://community.songjukti.com/")}
                      >
                        <Typography
                          sx={{
                            color: tokens.color.teal,
                            fontFamily: tokens.font.body,
                            fontSize: "0.8125rem",
                            fontWeight: 600,
                          }}
                        >
                          See resources
                        </Typography>
                        <ArrowRightAlt sx={{ color: tokens.color.teal, fontSize: "1.15rem" }} />
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Stack>

          {/* Right-hand actions: auth buttons (tablet+), search icon (phones),
              hamburger (everything below lg) */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 0.5, md: 1.5 }}
            sx={{ flexShrink: 0 }}
          >
            {/* Auth actions (md and up) */}
            <Stack
              spacing={1.5}
              direction="row"
              alignItems="center"
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              <Button
                variant="outlined"
                sx={{
                  borderColor: tokens.color.line,
                  color: tokens.color.ink,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  "&:hover": {
                    borderColor: tokens.color.teal,
                    color: tokens.color.teal,
                    bgcolor: tokens.color.tealLight,
                  },
                }}
                onClick={handleDashboard}
              >
                {isLogged ? "Dashboard" : "Sign in"}
              </Button>
              {!isLogged && (
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: tokens.color.teal,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    "&:hover": { bgcolor: tokens.color.tealDark },
                  }}
                  onClick={() => window.open("https://user.songjukti.com/signup")}
                >
                  Sign up
                </Button>
              )}
            </Stack>

            {/* Search toggle: phones only (md+ has the inline search bar) */}
            <IconButton
              aria-label={mobileSearchOpen ? "close search" : "open search"}
              onClick={() => setMobileSearchOpen((open) => !open)}
              sx={{ color: tokens.color.ink, display: { md: "none" } }}
            >
              {mobileSearchOpen ? <Close /> : <Search />}
            </IconButton>

            {/* Hamburger: everything below lg */}
            <IconButton
              aria-label="open menu"
              onClick={handleDrawerToggle}
              sx={{ color: tokens.color.ink, display: { lg: "none" } }}
            >
              <MenuIcon />
            </IconButton>
          </Stack>
        </Stack>

        {/* Mobile: expandable full-width search */}
        <Collapse in={mobileSearchOpen} sx={{ display: { md: "none" } }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              px: { xs: 1.5, sm: 2.5 },
              pb: 1.5,
              borderTop: `1px solid ${tokens.color.line}`,
              pt: 1.5,
            }}
          >
            <Select
              variant="standard"
              value={searchType}
              onChange={handleTypeChange}
              displayEmpty
              disableUnderline
              size="small"
              inputProps={{ "aria-label": "Search category" }}
              sx={{
                flexShrink: 0,
                bgcolor: tokens.input.bgMuted,
                borderRadius: `${tokens.radius.sm}px`,
                px: 1.5,
                py: 0.5,
                fontFamily: tokens.font.body,
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: tokens.color.inkMuted,
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="job">Job</MenuItem>
              <MenuItem value="service">Service</MenuItem>
              <MenuItem value="freelancer">Freelancer</MenuItem>
              <MenuItem value="agency">Agency</MenuItem>
            </Select>

            <TextField
              variant="standard"
              autoFocus
              fullWidth
              value={search}
              onChange={handleChange}
              onKeyDown={handleSearchKeyDown}
              placeholder={`Search ${searchType || "jobs"}`}
              sx={{
                minWidth: 0,
                bgcolor: tokens.input.bgMuted,
                borderRadius: `${tokens.radius.sm}px`,
                px: 1.5,
                py: 0.25,
                "& input": {
                  // 16px stops iOS Safari from zooming in on focus
                  fontFamily: tokens.font.body,
                  fontSize: "1rem",
                  color: tokens.color.ink,
                },
              }}
              InputProps={{ disableUnderline: true }}
            />

            <IconButton
              aria-label="search"
              onClick={handleSearchSubmit}
              sx={{
                flexShrink: 0,
                bgcolor: tokens.color.teal,
                color: tokens.color.white,
                "&:hover": { bgcolor: tokens.color.tealDark },
              }}
            >
              <Search fontSize="small" />
            </IconButton>
          </Stack>
        </Collapse>
      </Box>

      {/* Spacer so page content clears the fixed header */}
      <Box sx={{ height: HEADER_HEIGHT, width: "100%" }} />

      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              // never wider than the screen on very small phones
              width: `min(${drawerWidth}px, 85vw)`,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </Box>
  );
};

export default Header;